/**
 * Shared Safe-Mode state for the agentic surface — ONE query, one status
 * vocabulary, so every page reflects the same server truth.
 *
 * Reads GET /api/agentic/safe-mode (backend/app/routes/agentic_config.py →
 * services/agentic/safe_mode.get_safe_mode_state). The status never
 * collapses "we do not know" into "operating normally": `inactive` is only
 * reported when the server said `active === false`; a load error is
 * `unknown` (controls disable, header says so) and an entitlement denial
 * (403/404) is `not_entitled` (render nothing — the page itself shows
 * NotEnabled).
 *
 * P12-AG03 (2026-09-16): before this hook, PoliciesPage rendered "Operating
 * normally" whenever its safe-mode query had not resolved — including on a
 * 500 — because it tested `safeMode?.active` (falsy for undefined).
 *
 * Components that render this state live in ./safeMode.tsx (kept separate so
 * that file exports only components — react-refresh requirement).
 */
import { useQuery } from '@tanstack/react-query';
import { getSafeMode, type SafeModeState } from '../api/agentic';
import { isNotEntitled } from '../api/client';

export const SAFE_MODE_QUERY_KEY = ['agentic', 'safe-mode'] as const;

export type SafeModeStatus = 'loading' | 'active' | 'inactive' | 'unknown' | 'not_entitled';

export interface UseSafeModeResult {
  status: SafeModeStatus;
  state: SafeModeState | undefined;
  error: unknown;
}

export function deriveSafeModeStatus(
  isLoading: boolean,
  error: unknown,
  data: SafeModeState | undefined,
): SafeModeStatus {
  if (isLoading) return 'loading';
  if (error) return isNotEntitled(error) ? 'not_entitled' : 'unknown';
  if (data?.active === true) return 'active';
  if (data?.active === false) return 'inactive';
  return 'unknown';
}

export function useSafeMode(): UseSafeModeResult {
  const q = useQuery({ queryKey: SAFE_MODE_QUERY_KEY, queryFn: getSafeMode });
  return {
    status: deriveSafeModeStatus(q.isLoading, q.error, q.data),
    state: q.data,
    error: q.error,
  };
}
